/**
 * The router Start boots, and the query cache hanging off it.
 *
 * One factory for both sides: the prerender pass calls it once to write the
 * shell, the browser calls it again to hydrate. Nothing here may reach for
 * the DOM — the deck does that, from inside its own route.
 */

import { QueryClient } from '@tanstack/solid-query';
import { createRouter } from '@tanstack/solid-router';
import { routeTree } from './routeTree.gen.ts';

export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Query defaults, tuned for an API that charges for answers.
 *
 * The free YouTube Data tier is 10,000 units a day and a search costs 100 of
 * them, so a refetch nobody asked for is real money. Nothing revalidates on
 * focus or reconnect; a listing stays fresh for five minutes and lingers in
 * the cache for half an hour, which is how reopening the library is instant
 * and free.
 *
 * Nor is a refusal retried. Almost every way this API says no — an exhausted
 * quota, a disabled API, a key that is not valid — is a fact about the
 * account, and asking again only spends what is left of the quota on the same
 * answer. A dropped connection is the exception: `fetch` rejects with a
 * TypeError before any request was billed, so that one is worth one more go.
 */
const STALE_MS = 5 * 60 * 1000;
const CACHE_MS = 30 * 60 * 1000;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_MS,
        gcTime: CACHE_MS,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: (count, error) => count < 1 && error instanceof TypeError,
      },
    },
  });
}

/* The return type is the whole point — every `Link` and `useParams` in the
   app is checked against it — so it is inferred rather than restated. */
export function getRouter() {
  const queryClient = createQueryClient();

  return createRouter({
    routeTree,
    context: { queryClient },
    /* Both documents are one small chunk each; fetch them on hover. */
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/solid-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
